import {
  CopyDateOption,
  CopyMode,
  ICopyDataFilter,
  ICopyOptions,
  ICopyTarget,
} from 'interfaces/ICopyPaddockData';
import { GUnit } from 'interfaces/IGlobalCatalogue';
import { IInput, IInputZone, IUnit } from 'interfaces/IInput';
import { IInputStep } from 'interfaces/IInputStep';
import {
  getDefaultInputTaskStatus,
  IInputTask,
  InputTaskCategory,
} from 'interfaces/IInputTask';
import { PartialUserProfile } from 'interfaces/IUserProfile';
import { duplicateTask } from 'pages/FarmsPage/Workspace/Tasks/taskFactory';
import { getInputYear } from 'utils/FarmUtils';
import { toHectaresOrNull } from 'utils/unitConversion';

import { applyCopyDataFilter, getCopySourceTasks } from './copyDataFilter';

/** The existing document a copy writes into, or null when it creates one; Add as new always creates. */
export const getExistingTargetId = (
  target: ICopyTarget,
  copyMode: CopyMode
): string | null => (copyMode === CopyMode.AddAsNew ? null : target.inputId);

// Scaled areas are rounded, so a ratio doesn't leave float noise like 12.000000001 in the grid.
const AREA_DECIMALS = 4;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(.*)$/;

const isLeapYear = (year: number) =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

/**
 * Moves a stored date (`YYYY-MM-DD…`) by whole years, keeping its month, day
 * and time; 29 Feb lands on 28 Feb in a non-leap year. Read as text, so the
 * user's timezone can't move it a day.
 */
export const shiftIsoYear = (
  date: string | null,
  years: number
): string | null => {
  if (!date || years === 0) return date;
  const match = ISO_DATE_PATTERN.exec(date);
  if (!match) return date;
  const [, year, month, day, rest] = match;
  const shiftedYear = Number(year) + years;
  const shiftedDay =
    month === '02' && day === '29' && !isLeapYear(shiftedYear) ? '28' : day;
  return `${String(shiftedYear).padStart(4, '0')}-${month}-${shiftedDay}${rest}`;
};

/**
 * The year a Shift moves dates from: the source's own, or for an undated
 * source the earliest date among the tasks and steps it copies; null when
 * none is dated.
 */
export const getCopySourceYear = (
  source: IInput,
  sourceMode: InputTaskCategory,
  filter: ICopyDataFilter
): number | null => {
  const documentYear = getInputYear(source);
  if (documentYear !== null) return documentYear;
  const taskYears = applyCopyDataFilter(
    getCopySourceTasks(source, sourceMode),
    filter
  )
    .flatMap(({ task, steps }) => [
      task.startDate,
      task.endDate,
      ...steps.flatMap((step) => [step.startDate, step.endDate]),
    ])
    .map((date) => (date ? ISO_DATE_PATTERN.exec(date) : null))
    .filter((match): match is RegExpExecArray => match !== null)
    .map((match) => Number(match[1]));
  return taskYears.length > 0 ? Math.min(...taskYears) : null;
};

/** The years a Shift moves dates by: from the source's year to the destination's. */
export const getCopyYearOffset = (
  sourceYear: number | null,
  destinationYear: number
): number => (sourceYear === null ? 0 : destinationYear - sourceYear);

const copyDate = (
  date: string | null,
  option: CopyDateOption,
  yearOffset: number
): string | null => {
  if (option === CopyDateOption.Remove) return null;
  if (option === CopyDateOption.Keep) return date;
  return shiftIsoYear(date, yearOffset);
};

/** The source and destination areas a Keep area off copy scales by. */
export interface CopyAreaScale {
  /** The source document's area in ha; null when unknown. */
  sourceAreaHa: number | null;
  /** The destination's area, in its own unit. */
  destinationArea: number | null;
  destinationAreaUnit: IUnit | null;
}

export const getCopyAreaScale = (
  sourceZone: IInputZone | null,
  destinationZone: IInputZone | null,
  gUnits: GUnit[]
): CopyAreaScale => ({
  sourceAreaHa: toHectaresOrNull(
    sourceZone?.zoneArea,
    sourceZone?.areaUnit,
    gUnits
  ),
  destinationArea: destinationZone?.zoneArea ?? null,
  destinationAreaUnit: destinationZone?.areaUnit ?? null,
});

const roundArea = (area: number) =>
  Math.round(area * 10 ** AREA_DECIMALS) / 10 ** AREA_DECIMALS;

/**
 * A task or step area scaled to the destination: the same share of the
 * destination's area as it had of the source's, in the destination's unit.
 * With either area unknown (no source area, or a unit that can't be
 * converted) it covers the whole destination; with no destination area it's
 * left as it is. A zero area stays zero, and an empty area stays empty, so it
 * keeps falling back to the task or zone area.
 */
export const scaleCopyArea = (
  area: number | null,
  areaUnit: IUnit | null,
  scale: CopyAreaScale,
  gUnits: GUnit[]
): { area: number | null; areaUnit: IUnit | null } => {
  const { sourceAreaHa, destinationArea, destinationAreaUnit } = scale;
  if (area === null || !destinationArea || destinationArea <= 0) {
    return { area, areaUnit };
  }
  // Checked before converting, which reads 0 as unknown.
  if (area === 0) return { area: 0, areaUnit: destinationAreaUnit };
  const areaHa = toHectaresOrNull(area, areaUnit, gUnits);
  const share = sourceAreaHa && areaHa ? areaHa / sourceAreaHa : 1;
  return {
    area: roundArea(destinationArea * share),
    areaUnit: destinationAreaUnit,
  };
};

interface BuildCopiedTasksParams {
  source: IInput;
  sourceMode: InputTaskCategory;
  filter: ICopyDataFilter;
  /** The destination's Plan or Actual; copied tasks take it. */
  destinationMode: InputTaskCategory;
  /** The zone the tasks land in: its links file them under its paddock. */
  destinationZone: IInputZone | null;
  options: ICopyOptions;
  yearOffset: number;
  gUnits: GUnit[];
  userProfile?: PartialUserProfile;
}

/**
 * The source tasks a filter keeps, ready for one destination: new task and
 * step ids (a Seed Treatment re-linked to its Seed's copy, or unlinked when
 * Exact match left the Seed out), the destination's category, links and
 * default status, the copy options' areas and dates, and no copy history.
 * The source tasks aren't changed.
 */
export const buildCopiedTasks = ({
  source,
  sourceMode,
  filter,
  destinationMode,
  destinationZone,
  options,
  yearOffset,
  gUnits,
  userProfile,
}: BuildCopiedTasksParams): IInputTask[] => {
  const scale = getCopyAreaScale(source.zone, destinationZone, gUnits);
  const fitArea = <T extends { area: number | null; areaUnit: IUnit | null }>(
    item: T
  ): T =>
    options.keepArea
      ? item
      : { ...item, ...scaleCopyArea(item.area, item.areaUnit, scale, gUnits) };
  const fitDates = <
    T extends { startDate: string | null; endDate: string | null },
  >(
    item: T
  ): T => ({
    ...item,
    startDate: copyDate(item.startDate, options.dateOption, yearOffset),
    endDate: copyDate(item.endDate, options.dateOption, yearOffset),
  });

  return applyCopyDataFilter(
    getCopySourceTasks(source, sourceMode),
    filter
  ).map(({ task, steps }) => {
    const copy = duplicateTask({ ...task, steps }, userProfile);
    const stepIds = new Set(copy.steps.map((step) => step.stepId));
    return fitDates(
      fitArea({
        ...copy,
        category: destinationMode,
        status: getDefaultInputTaskStatus(destinationMode),
        links: destinationZone?.links ? [...destinationZone.links] : [],
        steps: copy.steps.map((step): IInputStep => {
          const linked =
            'linkedStepId' in step &&
            step.linkedStepId &&
            !stepIds.has(step.linkedStepId)
              ? ({ ...step, linkedStepId: null } as IInputStep)
              : step;
          return fitDates(fitArea(linked));
        }),
      })
    );
  });
};

const copyCrop = (source: IInput): IInput['crop'] => ({
  ...source.crop,
  variety: source.crop?.variety ? { ...source.crop.variety } : null,
});

/**
 * Replace: the destination mode's tasks swapped for the copied ones, and the
 * source's crop and variety. The other mode's tasks, and the document's own
 * name, dates and zone, stay.
 */
export const buildReplacedInput = (
  destination: IInput,
  source: IInput,
  copiedTasks: IInputTask[],
  destinationMode: InputTaskCategory
): IInput => ({
  ...destination,
  crop: copyCrop(source),
  tasks: [
    ...destination.tasks.filter((task) => task.category !== destinationMode),
    ...copiedTasks,
  ],
});

/**
 * Add to: the copied tasks after the existing ones, which are left as they
 * are. Document data stays, except crop and variety when ticked.
 */
export const buildAddedToInput = (
  destination: IInput,
  source: IInput,
  copiedTasks: IInputTask[],
  includeCropAndVariety: boolean
): IInput => ({
  ...destination,
  crop: includeCropAndVariety ? copyCrop(source) : destination.crop,
  tasks: [...destination.tasks, ...copiedTasks],
});

/**
 * A new document from a blank one for the destination paddock: the source's
 * crop and variety (a plan needs a crop) and the copied tasks. Its own dates
 * are the source's shifted to the destination year whatever the Dates option
 * (which only applies to tasks), so it files under the year it was copied
 * into; with no start date it starts on 1 January.
 */
export const buildNewCopyInput = (
  blank: IInput,
  source: IInput,
  copiedTasks: IInputTask[],
  yearOffset: number,
  destinationYear: number
): IInput => {
  const startDate =
    shiftIsoYear(source.startDate, yearOffset) ??
    `${destinationYear}-01-01T00:00:00.000Z`;
  const endDate = shiftIsoYear(source.endDate, yearOffset);
  return {
    ...blank,
    crop: copyCrop(source),
    startDate,
    // An end the 1 January fallback overtakes is dropped, so the plan isn't left ending before it starts.
    endDate: endDate && endDate >= startDate ? endDate : null,
    tasks: copiedTasks,
  };
};
